/**
 * experimentPlugin.js
 *
 * Fully functional colorimeter experiment plugin for the lab framework.
 * Contains all physics (Beer-Lambert law, Gaussian absorption, noise model)
 * extracted from ColorimeterEngine.js and Solution.js as pure functions.
 */

import { v4 as uuidv4 } from "uuid";

/**
 * Available wavelengths in the colorimeter.
 */
export const WAVELENGTHS = [
  { id: "blue", wavelength: 430 },
  { id: "blueGreen", wavelength: 470 },
  { id: "green", wavelength: 565 },
  { id: "red", wavelength: 635 }
];

/* ------------------------------------------------------------------ */
/*  Pure physics functions (extracted from ColorimeterEngine/Solution) */
/* ------------------------------------------------------------------ */

const MOLAR_CONVERSION_FACTOR = 1000;

/**
 * Gaussian absorption model: calculate epsilon at a given wavelength.
 * Extracted from Solution.getEpsilonAtWavelength().
 *
 * @param {number} wavelength - Measurement wavelength (nm)
 * @param {number} peakWavelength - Wavelength of maximum absorption (nm)
 * @param {number} peakEpsilon - Epsilon at peak wavelength
 * @param {number} bandWidth - Width of absorption peak (nm)
 * @returns {number} Epsilon at the given wavelength
 */
export function getEpsilonAtWavelength(
  wavelength,
  peakWavelength,
  peakEpsilon,
  bandWidth
) {
  const distance = wavelength - peakWavelength;
  const gaussianFactor = Math.exp(-0.5 * Math.pow(distance / bandWidth, 2));
  return peakEpsilon * gaussianFactor;
}

/**
 * Beer-Lambert law: A = epsilon * pathLength * concentration(M)
 * Extracted from ColorimeterEngine.calculateAbsorbance().
 *
 * @param {number} epsilon - Molar extinction coefficient at wavelength
 * @param {number} pathLength - Path length in cm
 * @param {number} concentrationMM - Concentration in millimoles/L (mM)
 * @returns {number} Absorbance (unitless)
 */
export function calculateAbsorbance(epsilon, pathLength, concentrationMM) {
  const concentrationM = concentrationMM / MOLAR_CONVERSION_FACTOR;
  return epsilon * pathLength * concentrationM;
}

/**
 * Compute absorbance for a solution at a given wavelength using Beer-Lambert.
 *
 * @param {object} solution - Solution plain object with peakWavelength, peakEpsilon, bandWidth, concentration
 * @param {number} wavelength - Current wavelength (nm)
 * @param {number} pathLength - Path length (cm)
 * @returns {number} Clean absorbance value
 */
function computeCleanAbsorbance(solution, wavelength, pathLength) {
  const epsilon = getEpsilonAtWavelength(
    wavelength,
    solution.peakWavelength,
    solution.peakEpsilon,
    solution.bandWidth
  );
  return calculateAbsorbance(epsilon, pathLength, solution.concentration);
}

/**
 * Apply noise model to a clean absorbance value.
 * Extracted from ColorimeterEngine.measure() lines 124-153.
 *
 * @param {number} absorbance - Clean absorbance
 * @param {object} config - { outlierProbability, outlierScaleFactor, calibrationDrift, measurementNoise }
 * @param {boolean} isCalibrated - Whether current wavelength is calibrated
 * @param {function} randomFn - () => number in [0,1)
 * @param {function} randomRangeFn - (min, max) => number in [min,max)
 * @returns {{ absorbanceNoisy: number, isOutlier: boolean, uncalibratedMeasurement: boolean }}
 */
function applyMeasurementNoise(
  absorbance,
  config,
  isCalibrated,
  randomFn,
  randomRangeFn
) {
  let absorbanceNoisy = absorbance;

  // Check for outlier
  const isOutlier = randomFn() < config.outlierProbability;
  if (isOutlier) {
    absorbanceNoisy = absorbanceNoisy * config.outlierScaleFactor;
  }

  // Apply calibration drift if not calibrated
  const uncalibratedMeasurement = !isCalibrated;
  if (uncalibratedMeasurement) {
    const drift = randomRangeFn(
      -config.calibrationDrift,
      config.calibrationDrift
    );
    absorbanceNoisy = absorbanceNoisy + drift;
  }

  // Add proportional noise
  const noiseFactor = randomRangeFn(
    -config.measurementNoise,
    config.measurementNoise
  );
  absorbanceNoisy = absorbanceNoisy * (1 + noiseFactor);

  // Ensure non-negative
  absorbanceNoisy = Math.max(0, absorbanceNoisy);

  return { absorbanceNoisy, isOutlier, uncalibratedMeasurement };
}

/**
 * Create a plain solution object (replaces new Solution()).
 *
 * @param {number} concentration - Concentration in mM
 * @param {string} color - Hex color code
 * @param {string} label - Display label for the cuvette
 * @param {object} absorptionParams - { peakWavelength, peakEpsilon, bandWidth, concentrationNoise }
 * @param {function} randomRangeFn - (min, max) => number
 * @returns {object} Plain solution object
 */
function createSolution({
  id,
  concentration,
  color,
  label,
  absorptionParams,
  randomRangeFn
}) {
  const concentrationNoise = absorptionParams.concentrationNoise ?? 0.02;
  const noiseFactor = randomRangeFn(-concentrationNoise, concentrationNoise);

  return {
    id: id || uuidv4(),
    concentration,
    concentrationNoisy: concentration * (1 + noiseFactor),
    color,
    label,
    peakWavelength: absorptionParams.peakWavelength ?? 480,
    peakEpsilon: absorptionParams.peakEpsilon ?? 3000,
    bandWidth: absorptionParams.bandWidth ?? 50
  };
}

/* ------------------------------------------------------------------ */
/*  Default configuration                                              */
/* ------------------------------------------------------------------ */

const DEFAULT_COLORIMETER_CONFIG = {
  pathLength: 1,
  calibrationDrift: 0.005,
  outlierScaleFactor: 3,
  outlierProbability: 0.02,
  measurementNoise: 0.02
};

const DEFAULT_SOLUTION_CONFIG = {
  peakWavelength: 480,
  peakEpsilon: 3000,
  bandWidth: 50,
  concentrationNoise: 0.02
};

/* ------------------------------------------------------------------ */
/*  Test solution definitions                                          */
/* ------------------------------------------------------------------ */

const TEST_SOLUTION_DEFS = [
  { id: "blank", concentration: 0.0, color: "#f0f8ff", label: "Blank" },
  {
    id: "solution-a",
    concentration: 0.05,
    color: "#ffe0e0",
    label: "A"
  },
  {
    id: "solution-b",
    concentration: 0.05,
    color: "#ffe0e0",
    label: "B"
  },
  {
    id: "solution-c",
    concentration: 0.05,
    color: "#ffe0e0",
    label: "C"
  },
  {
    id: "solution-d",
    concentration: 0.1,
    color: "#ffcccc",
    label: "D"
  },
  {
    id: "solution-e",
    concentration: 0.1,
    color: "#ffcccc",
    label: "E"
  },
  {
    id: "solution-f",
    concentration: 0.1,
    color: "#ffcccc",
    label: "F"
  },
  {
    id: "solution-g",
    concentration: 0.2,
    color: "#ff6666",
    label: "G"
  },
  {
    id: "solution-h",
    concentration: 0.2,
    color: "#ff6666",
    label: "H"
  },
  {
    id: "solution-i",
    concentration: 0.2,
    color: "#ff6666",
    label: "I"
  },
  {
    id: "solution-unknown",
    unknownRange: [0.02, 0.2],
    color: "#ff9999",
    label: "Unknown"
  }
];

const _calibrationStandardDefs = TEST_SOLUTION_DEFS.filter(
  (s) => s.id !== "blank" && s.id !== "solution-unknown"
);

/** Nine calibration standards A–I (excludes blank and unknown). */
export const CALIBRATION_STANDARD_SOLUTION_IDS = _calibrationStandardDefs.map(
  (s) => s.id
);

/** Map solution id → short label (e.g. solution-a → "A"). */
export const CALIBRATION_STANDARD_LABEL_BY_ID = Object.fromEntries(
  _calibrationStandardDefs.map((s) => [s.id, s.label])
);

/** Unknown sample id (matches TEST_SOLUTION_DEFS). */
export const UNKNOWN_SAMPLE_SOLUTION_ID = "solution-unknown";

/* ------------------------------------------------------------------ */
/*  Plugin factory                                                     */
/* ------------------------------------------------------------------ */

/**
 * Create the colorimeter experiment plugin.
 *
 * @param {object} [options]
 * @param {function} [options.random] - () => number in [0,1). Falls back to Math.random.
 * @param {function} [options.randomRange] - (min, max) => number. Falls back to simple impl.
 * @returns {{ name: string, register: function }}
 */
export function experimentPlugin(options = {}) {
  const randomFn = options.random || Math.random;
  const randomRangeFn =
    options.randomRange || ((min, max) => randomFn() * (max - min) + min);

  return {
    name: "colorimeter-experiment",

    register(ctx) {
      // ------------------------------------------------------------------
      // 1. Register entity types
      // ------------------------------------------------------------------

      ctx.registerEntityType("colorimeter", () => ({
        components: {
          device: {
            currentWavelengthIndex: 0,
            currentWavelength: WAVELENGTHS[0].wavelength,
            calibrationBaselines: {},
            isCalibrated: false,
            currentDisplayAbsorbance: null
          },
          sample: {
            currentSolutionId: null
          },
          measurements: {
            items: []
          }
        }
      }));

      // ------------------------------------------------------------------
      // 3. Register commands
      // ------------------------------------------------------------------

      /**
       * INIT_CONFIG — set up initial config and solutions map in meta.
       * payload: { colorimeterConfig?, solutionConfig? }
       */
      ctx.registerCommand("INIT_CONFIG", (state, payload = {}) => {
        return {
          patch: {
            meta: {
              config: {
                colorimeter: {
                  ...DEFAULT_COLORIMETER_CONFIG,
                  ...(payload.colorimeterConfig || {})
                },
                solution: {
                  ...DEFAULT_SOLUTION_CONFIG,
                  ...(payload.solutionConfig || {})
                }
              },
              solutions: {}
            }
          },
          events: [{ type: "CONFIG_INITIALIZED", payload: {} }]
        };
      });

      /**
       * SELECT_WAVELENGTH — change the colorimeter's active wavelength.
       * payload: { entityId: string, wavelengthIndex: number }
       */
      ctx.registerCommand("SELECT_WAVELENGTH", (state, payload) => {
        const { entityId, wavelengthIndex } = payload;
        const idx = Math.max(
          0,
          Math.min(wavelengthIndex, WAVELENGTHS.length - 1)
        );

        return {
          patch: {
            entities: {
              [entityId]: {
                components: {
                  device: {
                    currentWavelengthIndex: idx,
                    currentWavelength: WAVELENGTHS[idx].wavelength,
                    currentDisplayAbsorbance: null
                  }
                }
              }
            }
          },
          events: [
            {
              type: "WAVELENGTH_CHANGED",
              payload: { entityId, wavelength: WAVELENGTHS[idx].wavelength }
            }
          ]
        };
      });

      /**
       * INSERT_SAMPLE — insert a solution into the colorimeter.
       * payload: { entityId: string, solutionId: string }
       */
      ctx.registerCommand("INSERT_SAMPLE", (state, payload) => {
        const { entityId, solutionId } = payload;

        return {
          patch: {
            entities: {
              [entityId]: {
                components: {
                  sample: { currentSolutionId: solutionId },
                  device: { currentDisplayAbsorbance: null }
                }
              }
            }
          },
          events: [
            { type: "SAMPLE_INSERTED", payload: { entityId, solutionId } }
          ]
        };
      });

      /**
       * REMOVE_SAMPLE — eject the current sample.
       * payload: { entityId: string }
       */
      ctx.registerCommand("REMOVE_SAMPLE", (state, payload) => {
        const { entityId } = payload;

        return {
          patch: {
            entities: {
              [entityId]: {
                components: {
                  sample: { currentSolutionId: null },
                  device: { currentDisplayAbsorbance: null }
                }
              }
            }
          },
          events: [{ type: "SAMPLE_REMOVED", payload: { entityId } }]
        };
      });

      /**
       * CALIBRATE — calibrate at the current wavelength.
       * Computes baseline absorbance from the currently inserted solution
       * using Beer-Lambert law (clean, no noise).
       * payload: { entityId: string }
       */
      ctx.registerCommand("CALIBRATE", (state, payload) => {
        const { entityId } = payload;
        const entity = state.entities[entityId];
        const device = entity.components.device;
        const solutionId = entity.components.sample.currentSolutionId;
        const solution = state.meta.solutions[solutionId];
        const config = state.meta.config.colorimeter;

        // Compute baseline absorbance (clean, no noise)
        const baseline = solution
          ? computeCleanAbsorbance(
              solution,
              device.currentWavelength,
              config.pathLength
            )
          : 0;

        return {
          patch: {
            entities: {
              [entityId]: {
                components: {
                  device: {
                    calibrationBaselines: {
                      [device.currentWavelength]: baseline
                    },
                    isCalibrated: true
                  }
                }
              }
            }
          },
          events: [
            {
              type: "CALIBRATED",
              payload: { entityId, wavelength: device.currentWavelength }
            }
          ]
        };
      });

      /**
       * MEASURE — take an absorbance measurement.
       * Full Beer-Lambert + noise model ported from ColorimeterEngine.measure().
       * payload: { entityId: string }
       */
      ctx.registerCommand("MEASURE", (state, payload) => {
        const { entityId } = payload;
        const entity = state.entities[entityId];
        const device = entity.components.device;
        const measurements = entity.components.measurements;
        const solutionId = entity.components.sample.currentSolutionId;
        const solution = state.meta.solutions[solutionId];
        const config = state.meta.config.colorimeter;

        // Calculate clean absorbance using Beer-Lambert
        let absorbance = computeCleanAbsorbance(
          solution,
          device.currentWavelength,
          config.pathLength
        );

        // Check if current wavelength is calibrated
        const isCalibrated = Object.prototype.hasOwnProperty.call(
          device.calibrationBaselines,
          device.currentWavelength
        );
        const baseline =
          device.calibrationBaselines[device.currentWavelength] ?? 0;

        // Apply calibration baseline subtraction (clean)
        if (isCalibrated) {
          absorbance = absorbance - baseline;
        }

        // Clamp to non-negative
        absorbance = Math.max(0, absorbance);

        // Apply noise model
        const noiseResult = applyMeasurementNoise(
          absorbance,
          config,
          isCalibrated,
          randomFn,
          randomRangeFn
        );

        const measurementResult = {
          solutionId,
          absorbance,
          absorbanceNoisy: noiseResult.absorbanceNoisy,
          wavelength: device.currentWavelength,
          isOutlier: noiseResult.isOutlier,
          uncalibratedMeasurement: noiseResult.uncalibratedMeasurement,
          concentration: solution.concentration,
          concentrationNoisy: solution.concentrationNoisy,
          isUnknown: !!solution.isUnknown,
          timestamp: new Date().toISOString()
        };

        return {
          patch: {
            entities: {
              [entityId]: {
                components: {
                  measurements: {
                    items: [...measurements.items, measurementResult]
                  },
                  device: {
                    currentDisplayAbsorbance: noiseResult.absorbanceNoisy
                  }
                }
              }
            }
          },
          events: [
            {
              type: "MEASUREMENT_TAKEN",
              payload: { entityId, result: measurementResult }
            }
          ]
        };
      });

      /**
       * GENERATE_SOLUTIONS — create the 10 test solutions and store in meta.
       * payload: {} (uses config from state.meta.config.solution)
       */
      // Uses `state` (not `patch`) — needs full replacement of the solutions map
      ctx.registerCommand("GENERATE_SOLUTIONS", (state) => {
        const solConfig =
          state.meta.config?.solution || DEFAULT_SOLUTION_CONFIG;
        const absorptionParams = {
          peakWavelength: solConfig.peakWavelength,
          peakEpsilon: solConfig.peakEpsilon,
          bandWidth: solConfig.bandWidth,
          concentrationNoise: solConfig.concentrationNoise
        };

        const solutions = {};
        const solutionList = [];
        for (const def of TEST_SOLUTION_DEFS) {
          const concentration = def.unknownRange
            ? randomRangeFn(def.unknownRange[0], def.unknownRange[1])
            : def.concentration;
          const sol = createSolution({
            id: def.id,
            concentration,
            color: def.color,
            label: def.label,
            absorptionParams,
            randomRangeFn
          });
          if (def.unknownRange) {
            sol.concentrationNoisy = 0;
            sol.isUnknown = true;
          }
          solutions[sol.id] = sol;
          solutionList.push({
            id: sol.id,
            concentration: sol.concentration,
            color: sol.color,
            label: sol.label
          });
        }

        return {
          state: {
            ...state,
            meta: {
              ...state.meta,
              solutions
            }
          },
          events: [
            {
              type: "SOLUTIONS_GENERATED",
              payload: { count: solutionList.length, solutions: solutionList }
            }
          ]
        };
      });

      /**
       * RESET_EXPERIMENT — remove the colorimeter entity, clear solutions,
       * then re-add entity and regenerate solutions.
       * payload: { entityId: string }
       */
      // Uses `state` (not `patch`) — needs full replacement of entity and solutions
      ctx.registerCommand("RESET_EXPERIMENT", (state, payload) => {
        const { entityId } = payload;
        const solConfig =
          state.meta.config?.solution || DEFAULT_SOLUTION_CONFIG;
        const absorptionParams = {
          peakWavelength: solConfig.peakWavelength,
          peakEpsilon: solConfig.peakEpsilon,
          bandWidth: solConfig.bandWidth,
          concentrationNoise: solConfig.concentrationNoise
        };

        // Generate new solutions
        const solutions = {};
        const solutionList = [];
        for (const def of TEST_SOLUTION_DEFS) {
          const concentration = def.unknownRange
            ? randomRangeFn(def.unknownRange[0], def.unknownRange[1])
            : def.concentration;
          const sol = createSolution({
            id: def.id,
            concentration,
            color: def.color,
            label: def.label,
            absorptionParams,
            randomRangeFn
          });
          if (def.unknownRange) {
            sol.concentrationNoisy = 0;
            sol.isUnknown = true;
          }
          solutions[sol.id] = sol;
          solutionList.push({
            id: sol.id,
            concentration: sol.concentration,
            color: sol.color,
            label: sol.label
          });
        }

        // Build fresh colorimeter entity with defaults
        const freshEntity = {
          id: entityId,
          type: "colorimeter",
          components: {
            device: {
              currentWavelengthIndex: 0,
              currentWavelength: WAVELENGTHS[0].wavelength,
              calibrationBaselines: {},
              isCalibrated: false,
              currentDisplayAbsorbance: null
            },
            sample: {
              currentSolutionId: null
            },
            measurements: {
              items: []
            }
          }
        };

        return {
          state: {
            ...state,
            entities: {
              ...state.entities,
              [entityId]: freshEntity
            },
            meta: {
              ...state.meta,
              solutions
            }
          },
          events: [
            { type: "EXPERIMENT_RESET", payload: { entityId } },
            {
              type: "SOLUTIONS_GENERATED",
              payload: { count: solutionList.length, solutions: solutionList }
            }
          ]
        };
      });

      /**
       * LOAD_MEASUREMENTS — restore persisted measurements into an entity.
       * Used to hydrate the framework state from datastore on page refresh.
       * payload: { entityId: string, measurements: Array }
       */
      ctx.registerCommand("LOAD_MEASUREMENTS", (state, payload) => {
        const { entityId, measurements } = payload;
        const entity = state.entities[entityId];
        if (!entity) {
          return { error: { message: `Entity "${entityId}" not found` } };
        }
        return {
          patch: {
            entities: {
              [entityId]: {
                components: {
                  measurements: {
                    items: [...measurements]
                  },
                  device: { currentDisplayAbsorbance: null }
                }
              }
            }
          },
          events: [
            {
              type: "MEASUREMENTS_LOADED",
              payload: { entityId, count: measurements.length }
            }
          ]
        };
      });

      /**
       * LOAD_SOLUTIONS — restore persisted solutions into state.meta.solutions.
       * Used to hydrate the framework state from datastore on page refresh
       * so that solution IDs and concentrationNoisy values remain stable.
       * payload: { solutions: Object<string, SolutionObject> }
       */
      ctx.registerCommand("LOAD_SOLUTIONS", (state, payload) => {
        const { solutions } = payload;
        return {
          state: {
            ...state,
            meta: { ...state.meta, solutions }
          },
          events: [
            {
              type: "SOLUTIONS_LOADED",
              payload: { count: Object.keys(solutions).length }
            }
          ]
        };
      });

      /**
       * UPDATE_CONFIG — update colorimeter and/or solution config.
       * payload: { colorimeterConfig?, solutionConfig? }
       */
      ctx.registerCommand("UPDATE_CONFIG", (state, payload) => {
        return {
          patch: {
            meta: {
              config: {
                colorimeter: payload.colorimeterConfig || {},
                solution: payload.solutionConfig || {}
              }
            }
          },
          events: [{ type: "CONFIG_UPDATED", payload }]
        };
      });

      // ------------------------------------------------------------------
      // 4. Register validators
      // ------------------------------------------------------------------

      ctx.registerValidator((cmd, state) => {
        if (cmd.type === "MEASURE" || cmd.type === "CALIBRATE") {
          const entity = state.entities[cmd.payload?.entityId];
          if (!entity) {
            return {
              code: "ENTITY_NOT_FOUND",
              message: `Colorimeter "${cmd.payload?.entityId}" not found`,
              details: { entityId: cmd.payload?.entityId }
            };
          }
          if (!entity.components.sample?.currentSolutionId) {
            return {
              code: "NO_SAMPLE_INSERTED",
              message: "Insert a sample before measuring or calibrating",
              details: { entityId: cmd.payload?.entityId }
            };
          }
          // Verify the solution exists in meta
          const solId = entity.components.sample.currentSolutionId;
          if (!state.meta.solutions?.[solId]) {
            return {
              code: "SOLUTION_NOT_FOUND",
              message: `Solution "${solId}" not found in state`,
              details: { solutionId: solId }
            };
          }
        }

        if (cmd.type === "SELECT_WAVELENGTH") {
          const entity = state.entities[cmd.payload?.entityId];
          if (!entity) {
            return {
              code: "ENTITY_NOT_FOUND",
              message: `Colorimeter "${cmd.payload?.entityId}" not found`,
              details: { entityId: cmd.payload?.entityId }
            };
          }
        }

        return null;
      });
    }
  };
}
